import _extends from "@babel/runtime/helpers/extends";
import React, { useEffect, useRef, useState } from "react";
import Button from "../Button";
import ErrorIcon from "@mui/icons-material/Error";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Tooltip from "../Tooltip";
import classNames from "classnames";
import FormControl from "../FormControl";
import ContentCopyIcon from "mdi-material-ui/ContentCopy";
import { isWindowAvailable } from "../Services/windowService";
import { withPopoverText } from "../Utilities/HelperTextHelper";
import { useStyles } from "./styles";
export const TextInput = /*#__PURE__*/React.forwardRef((props, ref) => {
  const {
    id,
    label,
    disabled,
    error,
    errorMessage,
    helperText,
    rows,
    onChange,
    value,
    fullWidth,
    labelProps,
    inputComponent,
    inputProps,
    className,
    variant = "outlined",
    endAdornment,
    startAdornment,
    maxLength,
    autoComplete,
    inputRef,
    copy,
    margin = true,
    readOnly,
    minRows,
    maxRows,
    InputLabelProps,
    InputProps,
    popoverText,
    customHelperIcon,
    executeValidation,
    autoFocus,
    FormControlProps,
    ...otherProps
  } = props;
  const localInputRef = useRef();
  const classes = useStyles();
  const [didCopy, setDidCopy] = useState(false);
  const handleCopy = () => {
    if (!isWindowAvailable()) {
      return;
    }
    localInputRef.current?.select();
    document.execCommand("copy");
    localInputRef.current?.blur();
    setDidCopy(true);
    setTimeout(() => setDidCopy(false), 5000);
  };
  const copyEndAdornment = /*#__PURE__*/React.createElement(Tooltip, {
    title: "Copied!",
    open: didCopy
  }, /*#__PURE__*/React.createElement(InputAdornment, {
    position: "end"
  }, /*#__PURE__*/React.createElement(Button, {
    type: "icon",
    title: didCopy ? "" : "Copy",
    onClick: handleCopy
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.buttonTooltipFix
  }, /*#__PURE__*/React.createElement(ContentCopyIcon, {
    titleAccess: "copy"
  })))));
  const appliedLabelProps = {
    className: classNames("prmbl-textInput-label", classes[variant + "Label"]),
    classes: {
      shrink: classes[variant + "ShrunkLabel"],
      error: classes.error
    },
    ...labelProps,
    ...InputLabelProps,
    id: `${id}-label`
  };
  const appliedInputProps = {
    ...InputProps,
    inputComponent,
    inputProps: {
      maxLength: maxLength,
      disabled: disabled,
      ...inputProps
    },
    inputRef: el => {
      localInputRef.current = el;
      if (typeof inputRef === "function") inputRef(el);else if (inputRef) inputRef.current = el;
    },
    className: classNames("prmbl-textInput-input", classes[variant + "Input"], {
      [classes.customComponent]: inputComponent
    }),
    endAdornment: copy ? copyEndAdornment : endAdornment,
    startAdornment,
    classes: {
      input: classes[variant + "InputElement"]
    },
    autoComplete,
    autoFocus: autoFocus ?? false,
    readOnly: readOnly ?? false
  };
  const renderedErrorMessage = /*#__PURE__*/React.createElement("strong", {
    className: classes.errorMessageWrapper
  }, /*#__PURE__*/React.createElement(ErrorIcon, {
    className: classes.errorIcon
  }), /*#__PURE__*/React.createElement("span", null, errorMessage));
  const readOnlyOnChange = () => {
    // Do nothing
  };
  useEffect(() => {
    if (autoFocus) {
      localInputRef.current?.focus();
    }
  }, [autoFocus]);
  const renderedTextField = /*#__PURE__*/React.createElement(TextField, _extends({}, otherProps, {
    variant: variant === "compact" ? "outlined" : variant === "inline" ? "standard" : variant,
    id: id,
    label: label,
    className: classNames(className, "prmbl-textInput", classes.root, {
      [classes.error]: error,
      [classes.disableBorderBottomRadius]: errorMessage
    }, classes[variant + "Wrapper"]),
    value: value,
    onChange: readOnly ? readOnlyOnChange : onChange,
    disabled: disabled,
    "aria-disabled": disabled,
    error: error,
    minRows: minRows ? minRows : rows,
    inputRef: input => props.inputComponent = input,
    maxRows: maxRows,
    fullWidth: fullWidth,
    InputProps: appliedInputProps,
    InputLabelProps: {
      ...appliedLabelProps,
      shrink: value || appliedLabelProps.shrink ? true : false
    },
    FormHelperTextProps: {
      ...otherProps.FormHelperTextProps,
      classes: {
        ...otherProps.FormHelperTextProps?.classes,
        error: classNames(classes.error, otherProps.FormHelperTextProps?.classes?.error),
        contained: classNames(classes.helperText, otherProps.FormHelperTextProps?.classes?.contained)
      }
    },
    helperText: error && errorMessage ? renderedErrorMessage : withPopoverText(helperText, popoverText, customHelperIcon),
    ref: ref
  }));
  return /*#__PURE__*/React.createElement(FormControl, _extends({
    id: id,
    fullWidth: fullWidth,
    error: error,
    margin: margin ? "normal" : "none"
  }, FormControlProps), renderedTextField);
});
TextInput.displayName = "TextInput";
TextInput.defaultProps = {
  variant: "outlined",
  margin: true
};
export default TextInput;